import type { OperationValidationError } from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import type { ComponentProps } from 'react'

import { isOperationValidationError } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'

type Props = {
  /** Operation in scope, if any. Without one nothing is reported. */
  operationKey: Optional<string>
  /**
   * Narrows the reported errors further, for viewers that report part of the
   * errors of the operation in scope somewhere else.
   */
  errorFilter?: (error: OperationValidationError) => boolean
  /** Padding around the reported errors. Defaults to none. */
  padding?: ComponentProps<typeof Stack>['padding']
}

/**
 * Operation level validation errors of the operation in scope. Errors belonging to
 * other operations are reported where those operations are viewed.
 */
export const OperationValidationErrors = ({
  operationKey,
  errorFilter,
  padding,
}: Props) => {
  const { errors } = useOperationsContext()
  const translate = useTranslate()

  const errorsForOperationInScope = operationKey
    ? errors.filter(
        ({ error }) =>
          isOperationValidationError(error) &&
          error.operationKey === operationKey &&
          (errorFilter?.(error) ?? true),
      )
    : []

  return (
    !!errorsForOperationInScope.length && (
      <Stack className="flex-col" padding={padding}>
        {errorsForOperationInScope.map((validationError, index) => (
          <Alert
            // oxlint-disable-next-line react/no-array-index-key
            key={`${validationError.error.errorMessage}-${index}`}
            severity="error"
            title={
              validationError.error.errorMessage ?? translate('error.unknown')
            }
          />
        ))}
      </Stack>
    )
  )
}
